// Progress over weeks: training volume per muscle group, consistency streaks, and the main lifts' trends.
// Worked out on the server from the raw log; the rules live here so they're tested in one place.
import { z } from 'zod';
import { estimateOneRepMax } from './e1rm.ts';
import { addDays, daysBetween, IsoDate, startOfWeek } from './schedule.ts';
import { MuscleGroup, type ExerciseType } from './training.ts';
import { convertWeight } from './units.ts';
import type { SetRecord } from './workouts.ts';

/** A working set counts this much toward each main muscle, and half that toward each helper. */
export const PRIMARY_SET = 1;
export const SECONDARY_SET = 0.5;

export const ProgressWeek = z.object({
  /** Sunday the week starts. */
  start: IsoDate,
  workouts: z.number().int(),
  /** Working sets (warm-ups don't count). */
  sets: z.number().int(),
  /** Sets per muscle group, with helper muscles counting half. */
  muscles: z.partialRecord(MuscleGroup, z.number()),
});
export type ProgressWeek = z.infer<typeof ProgressWeek>;

export const ProgressResponse = z.object({
  userId: z.string(),
  today: IsoDate,
  /** Oldest first, ending with the current week. */
  weeks: z.array(ProgressWeek),
  /** Days with at least one workout, for the calendar grid (oldest first). */
  days: z.array(z.object({ date: IsoDate, workouts: z.number().int(), sets: z.number().int() })),
  /** In weeks with at least one workout. The current week only counts once it has a workout (it isn't over). */
  streak: z.object({ current: z.number().int(), longest: z.number().int() }),
  /** The most-trained weighted lifts in the period, with the best estimated 1RM (kg) per session. */
  lifts: z.array(z.object({ exerciseId: z.string(), sessions: z.number().int(), points: z.array(z.object({ date: IsoDate, value: z.number() })) })),
});
export type ProgressResponse = z.infer<typeof ProgressResponse>;

export type ProgressInput = {
  userId: string;
  today: string;
  /** How many weeks of volume and lift trends, including the current one. */
  weeks: number;
  /** How many weeks the calendar grid covers. */
  calendarWeeks: number;
  /** Every (non-deleted) workout ever: streaks need the whole history. */
  workouts: { id: string; date: string }[];
  /** Sets from the period, live ones only. */
  sets: Pick<SetRecord, 'workoutId' | 'exerciseId' | 'weight' | 'unit' | 'reps' | 'warmup'>[];
  exercises: Map<string, { type: ExerciseType; primaryMuscles: MuscleGroup[]; secondaryMuscles: MuscleGroup[] }>;
};

export function summarizeProgress(input: ProgressInput): ProgressResponse {
  const { today, exercises } = input;
  const thisWeek = startOfWeek(today);
  const firstWeek = addDays(thisWeek, -7 * (input.weeks - 1));
  const dateOf = new Map(input.workouts.map((w) => [w.id, w.date]));

  // --- Weekly volume ---
  const weeks: ProgressWeek[] = Array.from({ length: input.weeks }, (_, i) => ({ start: addDays(firstWeek, 7 * i), workouts: 0, sets: 0, muscles: {} }));
  const weekIndex = (date: string) => Math.floor(daysBetween(firstWeek, startOfWeek(date)) / 7);
  for (const w of input.workouts) {
    const i = weekIndex(w.date);
    if (i >= 0 && i < weeks.length) weeks[i]!.workouts++;
  }
  for (const s of input.sets) {
    const date = dateOf.get(s.workoutId);
    const exercise = exercises.get(s.exerciseId);
    if (!date || !exercise || s.warmup) continue;
    const i = weekIndex(date);
    if (i < 0 || i >= weeks.length) continue;
    const week = weeks[i]!;
    week.sets++;
    for (const m of exercise.primaryMuscles) week.muscles[m] = (week.muscles[m] ?? 0) + PRIMARY_SET;
    for (const m of exercise.secondaryMuscles) if (!exercise.primaryMuscles.includes(m)) week.muscles[m] = (week.muscles[m] ?? 0) + SECONDARY_SET;
  }

  // --- Calendar days ---
  const calendarFrom = addDays(thisWeek, -7 * (input.calendarWeeks - 1));
  const byDay = new Map<string, { date: string; workouts: number; sets: number }>();
  for (const w of input.workouts) {
    if (w.date < calendarFrom || w.date > today) continue;
    const d = byDay.get(w.date) ?? { date: w.date, workouts: 0, sets: 0 };
    d.workouts++;
    byDay.set(w.date, d);
  }
  for (const s of input.sets) {
    const d = byDay.get(dateOf.get(s.workoutId) ?? '');
    if (d && !s.warmup) d.sets++;
  }

  // --- Streaks: weeks in a row with a workout ---
  const active = new Set(input.workouts.map((w) => startOfWeek(w.date)));
  let current = 0;
  // This week counts once it has a workout; otherwise the streak can still be alive from last week.
  for (let week = active.has(thisWeek) ? thisWeek : addDays(thisWeek, -7); active.has(week); week = addDays(week, -7)) current++;
  let longest = 0;
  let run = 0;
  let prev: string | null = null;
  for (const week of [...active].sort()) {
    run = prev !== null && addDays(prev, 7) === week ? run + 1 : 1;
    longest = Math.max(longest, run);
    prev = week;
  }

  // --- Main lifts: most sessions in the period, best estimated 1RM per session ---
  const sessions = new Map<string, Map<string, number>>(); // exercise → date → best e1RM (kg)
  for (const s of input.sets) {
    const date = dateOf.get(s.workoutId);
    const exercise = exercises.get(s.exerciseId);
    if (!date || date < firstWeek || exercise?.type !== 'weight_reps' || s.warmup || s.weight === null || !s.reps) continue;
    const e1rm = estimateOneRepMax(convertWeight(s.weight, s.unit, 'kg'), s.reps);
    const perDate = sessions.get(s.exerciseId) ?? new Map<string, number>();
    perDate.set(date, Math.max(perDate.get(date) ?? 0, e1rm));
    sessions.set(s.exerciseId, perDate);
  }
  const lifts = [...sessions]
    .map(([exerciseId, perDate]) => ({
      exerciseId,
      sessions: perDate.size,
      points: [...perDate].sort(([a], [b]) => a.localeCompare(b)).map(([date, value]) => ({ date, value: Math.round(value * 10) / 10 })),
    }))
    .sort((a, b) => b.sessions - a.sessions || a.exerciseId.localeCompare(b.exerciseId))
    .slice(0, 4);

  return { userId: input.userId, today, weeks, days: [...byDay.values()].sort((a, b) => a.date.localeCompare(b.date)), streak: { current, longest }, lifts };
}
